import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { getHistoricoFundo, exportHistoricoFundo } from '../../../../services/carteiraDiariaService';
import { HistoricoFundoItem } from '../../../../types/carteiraDiaria';
import { formatBRL, formatQtd, formatDate } from '../../../../utils/formatters';
import { formatRent, thSx, tdSx, SortHeader, SortDir } from '../../../carteira/components/historicoTableUtils';

const PAGE_SIZE = 30;

export interface HistoricoFundoTabProps {
  fundoId: number;
}

const HistoricoFundoTab: React.FC<HistoricoFundoTabProps> = ({ fundoId }) => {
  const [rows, setRows]           = useState<HistoricoFundoItem[]>([]);
  const [count, setCount]         = useState(0);
  const [hasPfee, setHasPfee]     = useState(false);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sortDir, setSortDir]     = useState<SortDir>('desc');

  const ordering   = sortDir === 'desc' ? '-data_posicao' : 'data_posicao';
  const totalPages = Math.ceil(count / PAGE_SIZE);

  const load = useCallback(() => {
    setLoading(true);
    getHistoricoFundo(fundoId, page, PAGE_SIZE, ordering)
      .then(res => {
        setRows(res.results);
        setCount(res.count);
        if (res.has_pfee) setHasPfee(true);
      })
      .catch(() => { setRows([]); setCount(0); })
      .finally(() => setLoading(false));
  }, [fundoId, page, ordering]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportHistoricoFundo(fundoId);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (_f: string) => {
    setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    setPage(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ py: 3, color: '#8A98A8', textAlign: 'center', fontSize: '13px', fontFamily: 'Inter' }}>
        Sem histórico disponível para este fundo.
      </Typography>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#F8FAFC' }}>
      {/* Cabeçalho com botão de exportação */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, pt: 1.25, pb: 0.75 }}>
        <Tooltip title={exporting ? 'Gerando arquivo...' : `Exportar todos os ${count} registros como XLSX`}>
          <span>
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={exporting || count === 0}
              sx={{ p: 0.5, color: exporting ? '#B0BEC5' : '#2C91AD' }}
            >
              {exporting
                ? <CircularProgress size={14} sx={{ color: '#B0BEC5' }} />
                : <DownloadIcon sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <SortHeader label="Data" field="data_posicao" currentField="data_posicao" currentDir={sortDir} align="left" onSort={handleSort} />
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Qtd. Cotas</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Valor da Cota</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Patrimônio Líquido</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Variação (R$)</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Rent. Dia</TableCell>
              {hasPfee && (
                <TableCell sx={{ ...thSx, textAlign: 'right', color: '#E51739', cursor: 'default' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, justifyContent: 'flex-end' }}>
                    Taxa de Performance
                    <Tooltip
                      title={
                        <Box component="pre" sx={{ fontSize: '11px', fontFamily: 'Inter, monospace', whiteSpace: 'pre-wrap', m: 0 }}>
                          {[
                            'Valor da taxa de performance acumulada no dia.',
                            '',
                            'Armazenada como valor negativo nos passivos —',
                            'representa uma despesa deduzida do patrimônio.',
                            '',
                            'Ex: −R$ 10.000 significa que o fundo pagou',
                            'R$ 10.000 de taxa de performance naquele dia.',
                          ].join('\n')}
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: '#B0BEC5', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              )}
              {hasPfee && (
                <TableCell sx={{ ...thSx, textAlign: 'right', color: '#E51739', cursor: 'default' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, justifyContent: 'flex-end' }}>
                    Var. PFEE
                    <Tooltip
                      title={
                        <Box component="pre" sx={{ fontSize: '11px', fontFamily: 'Inter, monospace', whiteSpace: 'pre-wrap', m: 0 }}>
                          {[
                            'Variação percentual da taxa de performance',
                            'em relação ao dia anterior.',
                            '',
                            'Atenção: a lógica de cores é invertida.',
                            '',
                            '🟢 Negativo (ex: −20%) → taxa cresceu em',
                            '   valor absoluto → fundo performou mais.',
                            '',
                            '🔴 Positivo (ex: +20%) → taxa diminuiu →',
                            '   fundo performou menos.',
                            '',
                            'Fórmula: (pfee_hoje − pfee_ontem) / |pfee_ontem|',
                          ].join('\n')}
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: '#B0BEC5', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const rent = formatRent(r.rent_dia);
              return (
                <TableRow
                  key={r.data_posicao}
                  sx={{ '&:nth-of-type(even)': { backgroundColor: '#F4F7F9' } }}
                >
                  <TableCell sx={{ ...tdSx, fontWeight: 500 }}>{formatDate(r.data_posicao)}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    {r.quantidade != null ? formatQtd(r.quantidade) : '—'}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    {r.valor_por_unidade != null ? formatBRL(r.valor_por_unidade) : '—'}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>
                    {r.patrimonio_liquido != null ? formatBRL(r.patrimonio_liquido) : '—'}
                  </TableCell>
                  <TableCell sx={{
                    ...tdSx, textAlign: 'right', fontWeight: 500,
                    color: r.variacao_financeira == null ? '#8A98A8'
                      : r.variacao_financeira > 0 ? '#29A152'
                      : r.variacao_financeira < 0 ? '#E51739'
                      : '#8A98A8',
                  }}>
                    {r.variacao_financeira == null
                      ? '—'
                      : `${r.variacao_financeira >= 0 ? '+' : ''}${formatBRL(r.variacao_financeira)}`}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 500, color: rent.color, lineHeight: 1.3 }}>
                      {rent.text}
                    </Typography>
                  </TableCell>
                  {hasPfee && (
                    <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 500, color: r.pfee_valor != null ? '#E51739' : '#8A98A8' }}>
                      {r.pfee_valor != null ? formatBRL(r.pfee_valor) : '—'}
                    </TableCell>
                  )}
                  {hasPfee && (
                    <TableCell sx={{
                      ...tdSx, textAlign: 'right', fontWeight: 500,
                      color: r.pfee_variacao == null ? '#8A98A8'
                        : r.pfee_variacao < 0 ? '#29A152'
                        : r.pfee_variacao > 0 ? '#E51739'
                        : '#8A98A8',
                    }}>
                      {r.pfee_variacao == null
                        ? '—'
                        : `${r.pfee_variacao >= 0 ? '+' : ''}${r.pfee_variacao.toFixed(2)}%`}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Paginação */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 0.75, borderTop: '1px solid #EFF2F5' }}>
        <Typography sx={{ fontSize: '11px', color: '#8A98A8', fontFamily: 'Inter' }}>
          {count} registro{count !== 1 ? 's' : ''}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'Inter', mx: 0.5 }}>
          Página {page} de {totalPages}
        </Typography>
        <Tooltip title="Página anterior">
          <span>
            <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)} sx={{ p: 0.5 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Próxima página">
          <span>
            <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default HistoricoFundoTab;
